import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Eye,
  FileCheck2,
  FileText,
  Filter,
  Link2,
  Mail,
  MailCheck,
  MailQuestion,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCcw,
  Search,
  Send,
  ShieldCheck,
  UserCheck,
  WalletCards,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "com-pendencias"
  | "regras-ativas"
  | "aguardando-revisao"
  | "sem-regra";

type StatusRegraCliente =
  | "ativa"
  | "pendente_revisao"
  | "incompleta"
  | "inativa"
  | "sem_regra";

type TipoReferenciaComercial =
  | "sc"
  | "pi"
  | "po"
  | "pedido_compra"
  | "ordem_servico_cliente"
  | "contrato"
  | "nao_exige";

type PoliticaAgrupamentoNota =
  | "varios_servicos_mesma_nota"
  | "uma_nota_por_servico"
  | "uma_nota_por_pi_po"
  | "uma_nota_por_embarcacao"
  | "avaliar_caso_a_caso";

type DocumentoExigidoEnvio = "pdf" | "xml" | "pdf_xml" | "danfse" | "outro";

type CanalEnvioCliente = "email" | "portal" | "whatsapp" | "outro";

type TipoContatoFiscal =
  | "fiscal_cliente"
  | "solicitante"
  | "compras_cliente"
  | "financeiro_cliente"
  | "portal_cliente"
  | "outro";

type StatusItemRegra = "obrigatorio" | "opcional" | "nao_aplicavel";

type StatusValidacaoRegra =
  | "validada"
  | "pendente_validacao"
  | "desatualizada"
  | "sem_validacao";

type ContatoEnvioFiscal = {
  id: number;
  tipo: TipoContatoFiscal;
  nome: string;
  email: string | null;
  telefone: string | null;
  canal: CanalEnvioCliente;
  documento: DocumentoExigidoEnvio;
  observacao: string;
  status: StatusItemRegra;
};

type EtapaFluxoFiscal = {
  id: number;
  ordem: number;
  titulo: string;
  descricao: string;
  obrigatoria: boolean;
};

type HistoricoRegraCliente = {
  id: number;
  data: string;
  titulo: string;
  descricao: string;
  responsavel: string;
};

type RegraFiscalCliente = {
  id: number;
  clienteId: number;
  cliente: string;
  cnpjCpfCliente: string;
  grupoCliente: string;
  statusRegra: StatusRegraCliente;
  statusValidacao: StatusValidacaoRegra;
  responsavelInterno: string;
  revisadoEm: string | null;
  proximaRevisao: string | null;
  exigeScAntesEmissao: boolean;
  exigePiPoAntesEmissao: boolean;
  tipoReferenciaPrincipal: TipoReferenciaComercial;
  labelReferenciaPrincipal: string;
  referenciaObrigatoriaNaNota: boolean;
  textoReferenciaNaNota: string;
  politicaAgrupamentoNota: PoliticaAgrupamentoNota;
  permiteMultiplosServicosNaMesmaNota: boolean;
  exigeDescricaoIndividualPorServico: boolean;
  exigeAnexoRelatorioTecnico: boolean;
  exigeAnexoPedidoCompra: boolean;
  exigeLancamentoPortal: boolean;
  portalNome: string | null;
  portalUrl: string | null;
  prazoEnvioAposEmissaoHoras: number;
  observacoesEmissao: string;
  observacoesEnvio: string;
  contatosEnvio: ContatoEnvioFiscal[];
  fluxo: EtapaFluxoFiscal[];
  historico: HistoricoRegraCliente[];
};

type FilterState = {
  busca: string;
  statusRegra: string;
  statusValidacao: string;
  referencia: string;
  agrupamento: string;
  responsavel: string;
};

type RegrasFiscaisClientesLayoutProps = {
  section?: SectionKey;
};

const DEPARTMENT_COLOR = "#2f3f59";

const fixedRegras: RegraFiscalCliente[] = [
  {
    id: 1,
    clienteId: 101,
    cliente: "Bravante Navegação S.A.",
    cnpjCpfCliente: "33.333.333/0001-33",
    grupoCliente: "Bravante / Marlin",
    statusRegra: "ativa",
    statusValidacao: "validada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-05-08",
    proximaRevisao: "2026-08-08",
    exigeScAntesEmissao: true,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "pi",
    labelReferenciaPrincipal: "PI/PO",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o número do PI/PO nos dados complementares da NFS-e.",
    politicaAgrupamentoNota: "varios_servicos_mesma_nota",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: true,
    portalNome: "Portal de Fornecedores Bravante",
    portalUrl: "https://portal.bravante.com.br",
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Aguardar o recebimento da SC. Após a aprovação, aguardar o número de PI/PO. O PI/PO deve constar nos dados complementares da nota fiscal.",
    observacoesEnvio:
      "Após emitir a nota, enviar XML ao fiscal do cliente e PDF/DANFSe ao solicitante do serviço. Quando aplicável, anexar também no portal.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Departamento Fiscal Bravante",
        email: "fiscal@bravante.com.br",
        telefone: null,
        canal: "email",
        documento: "xml",
        observacao: "Enviar XML da NFS-e após emissão.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "solicitante",
        nome: "Solicitante do serviço",
        email: "solicitante@bravante.com.br",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar PDF/DANFSe para quem solicitou o serviço.",
        status: "obrigatorio",
      },
      {
        id: 3,
        tipo: "portal_cliente",
        nome: "Portal Bravante",
        email: null,
        telefone: null,
        canal: "portal",
        documento: "pdf_xml",
        observacao: "Anexar PDF e XML quando o processo exigir portal.",
        status: "obrigatorio",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Aguardar SC",
        descricao: "Não emitir nota antes do recebimento da SC do cliente.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Aguardar PI/PO",
        descricao:
          "Após a SC, aguardar o número de PI/PO que será mencionado na nota.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Emitir NFS-e",
        descricao:
          "Inserir o PI/PO nos dados complementares e discriminar os serviços executados.",
        obrigatoria: true,
      },
      {
        id: 4,
        ordem: 4,
        titulo: "Enviar documentos",
        descricao:
          "Enviar XML ao fiscal, DANFSe ao solicitante e anexar documentos no portal.",
        obrigatoria: true,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-08",
        titulo: "Regra validada",
        descricao:
          "Procedimento atualizado com exigência de SC, PI/PO e envio separado de XML/DANFSe.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 2,
    clienteId: 102,
    cliente: "Eustor Serviços Offshore Ltda",
    cnpjCpfCliente: "22.222.222/0001-22",
    grupoCliente: "Eustor",
    statusRegra: "ativa",
    statusValidacao: "validada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-05-07",
    proximaRevisao: "2026-08-07",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "po",
    labelReferenciaPrincipal: "PO",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o número do PO nos dados complementares de cada NFS-e.",
    politicaAgrupamentoNota: "uma_nota_por_servico",
    permiteMultiplosServicosNaMesmaNota: false,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Mesmo que o PO seja único, emitir uma NFS-e por serviço realizado. Não agrupar quatro serviços em uma única nota.",
    observacoesEnvio:
      "Enviar XML e DANFSe ao fiscal. Encaminhar cópia ao solicitante quando indicado no pedido.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Fiscal Eustor",
        email: "fiscal@eustor.com",
        telefone: null,
        canal: "email",
        documento: "pdf_xml",
        observacao: "Enviar XML e DANFSe de cada nota emitida.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "solicitante",
        nome: "Solicitante do serviço",
        email: "operacoes@eustor.com",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar DANFSe para conferência operacional.",
        status: "opcional",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Receber PO",
        descricao:
          "Confirmar número de PO antes de emitir qualquer nota fiscal.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Separar serviços",
        descricao:
          "Quando houver múltiplos serviços, emitir uma nota fiscal para cada serviço.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Emitir NFS-e",
        descricao:
          "Cada nota deve mencionar o mesmo PO quando os serviços estiverem vinculados ao mesmo pedido.",
        obrigatoria: true,
      },
      {
        id: 4,
        ordem: 4,
        titulo: "Enviar ao fiscal",
        descricao:
          "Enviar XML e DANFSe da nota para o e-mail fiscal cadastrado.",
        obrigatoria: true,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-07",
        titulo: "Regra criada",
        descricao:
          "Cadastro operacional criado com política de uma nota por serviço.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 3,
    clienteId: 103,
    cliente: "Googlemarine Serviços Marítimos Ltda",
    cnpjCpfCliente: "12.345.678/0001-90",
    grupoCliente: "Googlemarine",
    statusRegra: "ativa",
    statusValidacao: "pendente_validacao",
    responsavelInterno: "Guilherme Brito",
    revisadoEm: "2026-05-02",
    proximaRevisao: "2026-06-02",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: false,
    tipoReferenciaPrincipal: "ordem_servico_cliente",
    labelReferenciaPrincipal: "OS do cliente",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar a OS ou referência operacional do cliente nos dados complementares.",
    politicaAgrupamentoNota: "avaliar_caso_a_caso",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: false,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 48,
    observacoesEmissao:
      "Conferir com o solicitante se existe referência interna, OS ou identificação do vessel/case.",
    observacoesEnvio:
      "Enviar PDF/DANFSe ao solicitante e XML ao fiscal quando houver contato fiscal informado.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "solicitante",
        nome: "Solicitante Googlemarine",
        email: "solicitante@googlemarine.com",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar PDF/DANFSe ao solicitante.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "fiscal_cliente",
        nome: "Fiscal Googlemarine",
        email: "fiscal@googlemarine.com",
        telefone: null,
        canal: "email",
        documento: "xml",
        observacao: "Enviar XML quando solicitado pelo fiscal.",
        status: "opcional",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Conferir referência operacional",
        descricao:
          "Validar se o cliente possui OS, vessel, case ou referência interna para mencionar na nota.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Emitir nota",
        descricao:
          "Discriminar serviço e mencionar referência operacional nos complementares.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Enviar ao cliente",
        descricao:
          "Enviar DANFSe ao solicitante e XML ao fiscal quando aplicável.",
        obrigatoria: true,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-02",
        titulo: "Regra revisada",
        descricao:
          "Procedimento mantido com referência operacional e envio ao solicitante.",
        responsavel: "Guilherme Brito",
      },
    ],
  },
  {
    id: 4,
    clienteId: 104,
    cliente: "OSM Thome Brasil",
    cnpjCpfCliente: "55.555.555/0001-55",
    grupoCliente: "OSM Thome",
    statusRegra: "pendente_revisao",
    statusValidacao: "desatualizada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-03-12",
    proximaRevisao: "2026-05-12",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "pedido_compra",
    labelReferenciaPrincipal: "Pedido de compra",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o pedido de compra nos dados complementares.",
    politicaAgrupamentoNota: "uma_nota_por_pi_po",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: false,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: true,
    portalNome: "Portal OSM Thome",
    portalUrl: "https://portal.osmthome.com",
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Confirmar se o pedido de compra continua obrigatório para todas as emissões.",
    observacoesEnvio:
      "Procedimento precisa ser revisado antes da próxima emissão.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Fiscal OSM Thome",
        email: "fiscal@osmthome.com",
        telefone: null,
        canal: "email",
        documento: "pdf_xml",
        observacao: "Contato ainda precisa ser confirmado.",
        status: "obrigatorio",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Confirmar pedido de compra",
        descricao:
          "Validar com o cliente se o pedido de compra é obrigatório antes da emissão.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Emitir NFS-e",
        descricao:
          "Mencionar pedido de compra nos dados complementares da nota.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Enviar documentos",
        descricao:
          "Enviar PDF/XML e verificar necessidade de lançamento no portal.",
        obrigatoria: true,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-03-12",
        titulo: "Regra importada",
        descricao:
          "Procedimento cadastrado a partir de orientação antiga. Necessita revisão.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 5,
    clienteId: 105,
    cliente: "Vision Marine Electronics",
    cnpjCpfCliente: "Não informado",
    grupoCliente: "Vision Marine",
    statusRegra: "incompleta",
    statusValidacao: "sem_validacao",
    responsavelInterno: "Comercial",
    revisadoEm: null,
    proximaRevisao: null,
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: false,
    tipoReferenciaPrincipal: "nao_exige",
    labelReferenciaPrincipal: "Não definido",
    referenciaObrigatoriaNaNota: false,
    textoReferenciaNaNota: "Procedimento ainda não definido.",
    politicaAgrupamentoNota: "avaliar_caso_a_caso",
    permiteMultiplosServicosNaMesmaNota: false,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: false,
    exigeAnexoPedidoCompra: false,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 48,
    observacoesEmissao:
      "Cliente ainda sem regra fiscal validada. Confirmar dados antes da primeira emissão.",
    observacoesEnvio:
      "Cadastrar e-mail fiscal e solicitante antes de emitir a primeira nota.",
    contatosEnvio: [],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Completar cadastro",
        descricao:
          "Solicitar CNPJ, e-mail fiscal, solicitante e regra de emissão.",
        obrigatoria: true,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-09",
        titulo: "Cadastro iniciado",
        descricao: "Cliente criado sem procedimento fiscal validado.",
        responsavel: "Comercial",
      },
    ],
  },
];

const initialFilters: FilterState = {
  busca: "",
  statusRegra: "",
  statusValidacao: "",
  referencia: "",
  agrupamento: "",
  responsavel: "",
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "com-pendencias":
      return "Com pendências";
    case "regras-ativas":
      return "Regras ativas";
    case "aguardando-revisao":
      return "Aguardando revisão";
    case "sem-regra":
      return "Sem regra";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

function getStatusRegraLabel(status: StatusRegraCliente) {
  switch (status) {
    case "ativa":
      return "Ativa";
    case "pendente_revisao":
      return "Pendente revisão";
    case "incompleta":
      return "Incompleta";
    case "inativa":
      return "Inativa";
    case "sem_regra":
      return "Sem regra";
    default:
      return "Indefinido";
  }
}

function getStatusValidacaoLabel(status: StatusValidacaoRegra) {
  switch (status) {
    case "validada":
      return "Validada";
    case "pendente_validacao":
      return "Pendente validação";
    case "desatualizada":
      return "Desatualizada";
    case "sem_validacao":
      return "Sem validação";
    default:
      return "Indefinido";
  }
}

function getReferenciaLabel(tipo: TipoReferenciaComercial) {
  switch (tipo) {
    case "sc":
      return "SC";
    case "pi":
      return "PI";
    case "po":
      return "PO";
    case "pedido_compra":
      return "Pedido de compra";
    case "ordem_servico_cliente":
      return "OS do cliente";
    case "contrato":
      return "Contrato";
    case "nao_exige":
      return "Não exige";
    default:
      return "Referência";
  }
}

function getPoliticaAgrupamentoLabel(tipo: PoliticaAgrupamentoNota) {
  switch (tipo) {
    case "varios_servicos_mesma_nota":
      return "Vários serviços na mesma nota";
    case "uma_nota_por_servico":
      return "Uma nota por serviço";
    case "uma_nota_por_pi_po":
      return "Uma nota por PI/PO";
    case "uma_nota_por_embarcacao":
      return "Uma nota por embarcação";
    case "avaliar_caso_a_caso":
      return "Avaliar caso a caso";
    default:
      return "Não definido";
  }
}

function getTipoContatoLabel(tipo: TipoContatoFiscal) {
  switch (tipo) {
    case "fiscal_cliente":
      return "Fiscal do cliente";
    case "solicitante":
      return "Solicitante";
    case "compras_cliente":
      return "Compras";
    case "financeiro_cliente":
      return "Financeiro do cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "outro":
      return "Outro";
    default:
      return "Contato";
  }
}

function getCanalLabel(canal: CanalEnvioCliente) {
  switch (canal) {
    case "email":
      return "E-mail";
    case "portal":
      return "Portal";
    case "whatsapp":
      return "WhatsApp";
    case "outro":
      return "Outro";
    default:
      return "Canal";
  }
}

function getDocumentoEnvioLabel(documento: DocumentoExigidoEnvio) {
  switch (documento) {
    case "pdf":
      return "PDF";
    case "xml":
      return "XML";
    case "pdf_xml":
      return "PDF + XML";
    case "danfse":
      return "DANFSe";
    case "outro":
      return "Outro";
    default:
      return "Documento";
  }
}

function getStatusItemRegraLabel(status: StatusItemRegra) {
  switch (status) {
    case "obrigatorio":
      return "Obrigatório";
    case "opcional":
      return "Opcional";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getStatusRegraVisual(status: StatusRegraCliente) {
  switch (status) {
    case "ativa":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <ShieldCheck size={12} />,
      };

    case "pendente_revisao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <Clock3 size={12} />,
      };

    case "incompleta":
    case "sem_regra":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };

    case "inativa":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getStatusValidacaoVisual(status: StatusValidacaoRegra) {
  switch (status) {
    case "validada":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente_validacao":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <MailQuestion size={12} />,
      };

    case "desatualizada":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <RefreshCcw size={12} />,
      };

    case "sem_validacao":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getStatusItemVisual(status: StatusItemRegra) {
  switch (status) {
    case "obrigatorio":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <ClipboardCheck size={12} />,
      };

    case "opcional":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getPendenciasRegra(regra: RegraFiscalCliente) {
  const pendencias: string[] = [];

  if (regra.statusRegra === "incompleta" || regra.statusRegra === "sem_regra") {
    pendencias.push("Regra fiscal incompleta");
  }

  if (
    regra.statusValidacao === "desatualizada" ||
    regra.statusValidacao === "sem_validacao"
  ) {
    pendencias.push("Validação pendente");
  }

  if (regra.contatosEnvio.length === 0) {
    pendencias.push("Nenhum contato de envio cadastrado");
  }

  if (
    regra.referenciaObrigatoriaNaNota &&
    regra.tipoReferenciaPrincipal === "nao_exige"
  ) {
    pendencias.push("Referência obrigatória não definida");
  }

  if (regra.exigeLancamentoPortal && !regra.portalNome) {
    pendencias.push("Portal obrigatório não cadastrado");
  }

  return pendencias;
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper: string;
  tone: "neutral" | "success" | "warning" | "danger";
  icon: React.ReactNode;
}> = ({ label, value, helper, tone, icon }) => {
  const iconStyle =
    tone === "success"
      ? styles.summaryIconOk
      : tone === "warning"
      ? styles.summaryIconWarning
      : tone === "danger"
      ? styles.summaryIconDanger
      : styles.summaryIcon;

  return (
    <div style={styles.summaryCard}>
      <div style={iconStyle}>{icon}</div>

      <div style={styles.summaryInfo}>
        <span style={styles.summaryLabel}>{label}</span>
        <strong style={styles.summaryValue}>{value}</strong>
        <span style={styles.summaryHelper}>{helper}</span>
      </div>
    </div>
  );
};

const RegraClienteRow: React.FC<{
  regra: RegraFiscalCliente;
  departmentColor: string;
  onOpen: (regra: RegraFiscalCliente) => void;
}> = ({ regra, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  const regraVisual = getStatusRegraVisual(regra.statusRegra);
  const validacaoVisual = getStatusValidacaoVisual(regra.statusValidacao);

  const pendencias = getPendenciasRegra(regra);

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? toRgba(departmentColor, 0.05) : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      title="Duplo clique para abrir as orientações do cliente"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(regra)}
    >
      <td style={styles.td}>
        <div style={styles.clientCell}>
          <div
            style={{
              ...styles.clientIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <Building2 size={15} />
          </div>

          <div style={styles.clientInfo}>
            <strong
              style={{
                ...styles.clientName,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {regra.cliente}
            </strong>
            <span style={styles.mutedText}>{regra.cnpjCpfCliente}</span>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.badgeStack}>
          <Badge
            background={regraVisual.background}
            color={regraVisual.color}
            border={regraVisual.border}
            icon={regraVisual.icon}
          >
            {getStatusRegraLabel(regra.statusRegra)}
          </Badge>

          <Badge
            background={validacaoVisual.background}
            color={validacaoVisual.color}
            border={validacaoVisual.border}
            icon={validacaoVisual.icon}
          >
            {getStatusValidacaoLabel(regra.statusValidacao)}
          </Badge>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{regra.labelReferenciaPrincipal}</div>
        <div style={styles.mutedText}>
          {regra.referenciaObrigatoriaNaNota
            ? "Obrigatória na nota"
            : "Não obrigatória"}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>
          {getPoliticaAgrupamentoLabel(regra.politicaAgrupamentoNota)}
        </div>
        <div style={styles.mutedText}>
          {regra.permiteMultiplosServicosNaMesmaNota
            ? "Permite agrupamento"
            : "Não agrupar serviços"}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.documentMini}>
          <span>{regra.exigeScAntesEmissao ? "Exige SC" : "Sem SC"}</span>
          <span>
            {regra.exigePiPoAntesEmissao ? "Exige PI/PO" : "Sem PI/PO"}
          </span>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.documentMini}>
          <span>{regra.contatosEnvio.length} contato(s)</span>
          <span>
            {regra.exigeLancamentoPortal
              ? regra.portalNome || "Portal obrigatório"
              : "Sem portal"}
          </span>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{formatDate(regra.revisadoEm)}</div>
        <div style={styles.mutedText}>
          Próxima {formatDate(regra.proximaRevisao)}
        </div>
      </td>

      <td style={styles.tdNoRightBorder}>
        <div style={styles.documentMini}>
          <span
            style={{
              color: pendencias.length > 0 ? "#9a3412" : "#166534",
            }}
          >
            {pendencias.length > 0
              ? `${pendencias.length} pendência(s)`
              : "Sem pendência"}
          </span>
          <span>{regra.responsavelInterno}</span>
        </div>
      </td>
    </tr>
  );
};

const RegraClienteDetailOverlay: React.FC<{
  regra: RegraFiscalCliente | null;
  departmentColor: string;
  onClose: () => void;
}> = ({ regra, departmentColor, onClose }) => {
  if (!regra) return null;

  const regraVisual = getStatusRegraVisual(regra.statusRegra);
  const validacaoVisual = getStatusValidacaoVisual(regra.statusValidacao);
  const pendencias = getPendenciasRegra(regra);

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>
              Financeiro / Orientações fiscais por cliente
            </div>

            <h2 style={styles.overlayTitle}>{regra.cliente}</h2>

            <p style={styles.overlaySubtitle}>
              Procedimento de emissão, referências obrigatórias, agrupamento de
              serviços, contatos de envio e validação operacional.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailTopGrid}>
            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Status da regra</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={regraVisual.background}
                  color={regraVisual.color}
                  border={regraVisual.border}
                  icon={regraVisual.icon}
                >
                  {getStatusRegraLabel(regra.statusRegra)}
                </Badge>
              </div>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Validação</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={validacaoVisual.background}
                  color={validacaoVisual.color}
                  border={validacaoVisual.border}
                  icon={validacaoVisual.icon}
                >
                  {getStatusValidacaoLabel(regra.statusValidacao)}
                </Badge>
              </div>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Última revisão</span>
              <strong style={styles.detailValue}>
                {formatDate(regra.revisadoEm)}
              </strong>
              <span style={styles.detailMeta}>
                Próxima {formatDate(regra.proximaRevisao)}
              </span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Responsável interno</span>
              <strong style={styles.detailValue}>
                {regra.responsavelInterno}
              </strong>
              <span style={styles.detailMeta}>{regra.grupoCliente}</span>
            </div>
          </section>

          {pendencias.length > 0 && (
            <section style={styles.warningBox}>
              <div style={styles.warningHeader}>
                <AlertTriangle size={15} />
                <strong>Pendências da regra</strong>
              </div>

              <div style={styles.warningList}>
                {pendencias.map((pendencia) => (
                  <span key={pendencia}>{pendencia}</span>
                ))}
              </div>
            </section>
          )}

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <Building2 size={15} />
                <strong>Cliente vinculado</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Cliente</span>
                  <strong>{regra.cliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>CNPJ / CPF</span>
                  <strong>{regra.cnpjCpfCliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Grupo</span>
                  <strong>{regra.grupoCliente}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <ReceiptText size={15} />
                <strong>Referência comercial</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Referência principal</span>
                  <strong>
                    {getReferenciaLabel(regra.tipoReferenciaPrincipal)}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Obrigatória nos complementares</span>
                  <strong>
                    {regra.referenciaObrigatoriaNaNota ? "Sim" : "Não"}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Texto de orientação</span>
                  <strong>{regra.textoReferenciaNaNota}</strong>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <FileCheck2 size={15} />
              <strong>Regra de emissão da nota fiscal</strong>
            </div>

            <div style={styles.ruleGrid}>
              <div style={styles.ruleBox}>
                <span>SC antes da emissão</span>
                <strong>
                  {regra.exigeScAntesEmissao ? "Exigir" : "Não exige"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>PI/PO antes da emissão</span>
                <strong>
                  {regra.exigePiPoAntesEmissao ? "Exigir" : "Não exige"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Agrupamento da nota</span>
                <strong>
                  {getPoliticaAgrupamentoLabel(regra.politicaAgrupamentoNota)}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Vários serviços na mesma nota</span>
                <strong>
                  {regra.permiteMultiplosServicosNaMesmaNota
                    ? "Permitido"
                    : "Não permitido"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Discriminar serviço individual</span>
                <strong>
                  {regra.exigeDescricaoIndividualPorServico ? "Sim" : "Não"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Relatório técnico</span>
                <strong>
                  {regra.exigeAnexoRelatorioTecnico ? "Exigir" : "Não exige"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Pedido de compra anexo</span>
                <strong>
                  {regra.exigeAnexoPedidoCompra ? "Exigir" : "Não exige"}
                </strong>
              </div>

              <div style={styles.ruleBox}>
                <span>Prazo de envio</span>
                <strong>
                  {regra.prazoEnvioAposEmissaoHoras}h após emissão
                </strong>
              </div>
            </div>

            <div style={styles.orientationBox}>
              <strong>Orientação para emissão</strong>
              <span>{regra.observacoesEmissao}</span>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <ClipboardCheck size={15} />
              <strong>Fluxo recomendado para o financeiro</strong>
            </div>

            <div style={styles.flowList}>
              {regra.fluxo.map((etapa) => (
                <div key={etapa.id} style={styles.flowItem}>
                  <div
                    style={{
                      ...styles.flowNumber,
                      background: toRgba(departmentColor, 0.1),
                      borderColor: toRgba(departmentColor, 0.25),
                      color: departmentColor,
                    }}
                  >
                    {etapa.ordem}
                  </div>

                  <div style={styles.flowContent}>
                    <div style={styles.flowTitleLine}>
                      <strong>{etapa.titulo}</strong>

                      {etapa.obrigatoria && (
                        <span style={styles.requiredText}>Obrigatório</span>
                      )}
                    </div>

                    <span>{etapa.descricao}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <MailCheck size={15} />
              <strong>Contatos e envio da nota</strong>
            </div>

            <div style={styles.envioIntro}>
              <strong>Orientação para envio</strong>
              <span>{regra.observacoesEnvio}</span>
            </div>

            <div style={styles.contactsGrid}>
              {regra.contatosEnvio.length === 0 && (
                <div style={styles.emptyContacts}>
                  Nenhum contato de envio cadastrado para este cliente.
                </div>
              )}

              {regra.contatosEnvio.map((contato) => {
                const visual = getStatusItemVisual(contato.status);

                return (
                  <div key={contato.id} style={styles.contactCard}>
                    <div style={styles.contactHeader}>
                      <div style={styles.contactTitleArea}>
                        <strong>{getTipoContatoLabel(contato.tipo)}</strong>
                        <span>{contato.nome}</span>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getStatusItemRegraLabel(contato.status)}
                      </Badge>
                    </div>

                    <div style={styles.contactMetaGrid}>
                      <div>
                        <span>Documento</span>
                        <strong>
                          {getDocumentoEnvioLabel(contato.documento)}
                        </strong>
                      </div>

                      <div>
                        <span>Canal</span>
                        <strong>{getCanalLabel(contato.canal)}</strong>
                      </div>

                      <div>
                        <span>E-mail</span>
                        <strong>{contato.email || "Não informado"}</strong>
                      </div>

                      <div>
                        <span>Telefone</span>
                        <strong>{contato.telefone || "Não informado"}</strong>
                      </div>
                    </div>

                    <div style={styles.contactObservation}>
                      {contato.observacao}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <Link2 size={15} />
                <strong>Portal do cliente</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Exige lançamento em portal</span>
                  <strong>{regra.exigeLancamentoPortal ? "Sim" : "Não"}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Nome do portal</span>
                  <strong>{regra.portalNome || "Não informado"}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>URL</span>
                  <strong>{regra.portalUrl || "Não informado"}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <Clock3 size={15} />
                <strong>Histórico da regra</strong>
              </div>

              <div style={styles.historyList}>
                {regra.historico.map((item) => (
                  <div key={item.id} style={styles.historyItem}>
                    <div style={styles.historyDot} />
                    <div>
                      <strong>{item.titulo}</strong>
                      <span>
                        {formatDate(item.data)} · {item.responsavel}
                      </span>
                      <p>{item.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Fechar
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Eye size={14} />
            Simular emissão
          </button>

          <button type="button" style={styles.secondaryButton}>
            <UserCheck size={14} />
            Validar regra
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Plus size={14} />
            Adicionar contato
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              background: departmentColor,
              borderColor: departmentColor,
            }}
          >
            <Pencil size={14} />
            Editar regra
          </button>
        </div>
      </div>
    </div>
  );
};

const RegrasFiscaisClientesLayout: React.FC<
  RegrasFiscaisClientesLayoutProps
> = ({ section }) => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedRegra, setSelectedRegra] = useState<RegraFiscalCliente | null>(
    null
  );

  const responsaveis = useMemo(() => {
    return Array.from(
      new Set(fixedRegras.map((regra) => regra.responsavelInterno))
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, []);

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "com-pendencias":
        return fixedRegras.filter(
          (regra) => getPendenciasRegra(regra).length > 0
        );

      case "regras-ativas":
        return fixedRegras.filter((regra) => regra.statusRegra === "ativa");

      case "aguardando-revisao":
        return fixedRegras.filter(
          (regra) =>
            regra.statusRegra === "pendente_revisao" ||
            regra.statusValidacao === "desatualizada" ||
            regra.statusValidacao === "pendente_validacao"
        );

      case "sem-regra":
        return fixedRegras.filter(
          (regra) =>
            regra.statusRegra === "sem_regra" ||
            regra.statusRegra === "incompleta"
        );

      case "visao-geral":
      default:
        return fixedRegras;
    }
  }, [activeSection]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);

    return filteredBySection.filter((regra) => {
      const buscaMatch =
        !busca ||
        [
          regra.cliente,
          regra.cnpjCpfCliente,
          regra.grupoCliente,
          regra.responsavelInterno,
          regra.labelReferenciaPrincipal,
          regra.textoReferenciaNaNota,
          regra.observacoesEmissao,
          regra.observacoesEnvio,
          regra.portalNome,
          regra.portalUrl,
          ...regra.contatosEnvio.map((contato) => contato.nome),
          ...regra.contatosEnvio.map((contato) => contato.email),
        ].some((field) => normalizeTextSearch(field).includes(busca));

      const statusRegraMatch =
        !filters.statusRegra || regra.statusRegra === filters.statusRegra;

      const statusValidacaoMatch =
        !filters.statusValidacao ||
        regra.statusValidacao === filters.statusValidacao;

      const referenciaMatch =
        !filters.referencia ||
        regra.tipoReferenciaPrincipal === filters.referencia;

      const agrupamentoMatch =
        !filters.agrupamento ||
        regra.politicaAgrupamentoNota === filters.agrupamento;

      const responsavelMatch =
        !filters.responsavel ||
        regra.responsavelInterno === filters.responsavel;

      return (
        buscaMatch &&
        statusRegraMatch &&
        statusValidacaoMatch &&
        referenciaMatch &&
        agrupamentoMatch &&
        responsavelMatch
      );
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    const regrasAtivas = fixedRegras.filter(
      (regra) => regra.statusRegra === "ativa"
    ).length;

    const comPendencias = fixedRegras.filter(
      (regra) => getPendenciasRegra(regra).length > 0
    ).length;

    const aguardandoRevisao = fixedRegras.filter(
      (regra) =>
        regra.statusRegra === "pendente_revisao" ||
        regra.statusValidacao === "desatualizada" ||
        regra.statusValidacao === "pendente_validacao"
    ).length;

    const exigemPiPo = fixedRegras.filter(
      (regra) => regra.exigePiPoAntesEmissao
    ).length;

    return {
      totalClientes: fixedRegras.length,
      regrasAtivas,
      comPendencias,
      aguardandoRevisao,
      exigemPiPo,
    };
  }, []);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Orientações fiscais por cliente - ${getSectionTitle(
          activeSection
        )}`}
      />

      <div style={styles.content}>
        <section style={styles.summaryGrid}>
          <SummaryCard
            label="Clientes mapeados"
            value={resumo.totalClientes}
            helper="Clientes com regra fiscal cadastrada"
            tone="neutral"
            icon={<Building2 size={16} />}
          />

          <SummaryCard
            label="Regras ativas"
            value={resumo.regrasAtivas}
            helper="Procedimentos prontos para uso"
            tone="success"
            icon={<ShieldCheck size={16} />}
          />

          <SummaryCard
            label="Com pendências"
            value={resumo.comPendencias}
            helper="Regras incompletas ou sem validação"
            tone={resumo.comPendencias > 0 ? "warning" : "neutral"}
            icon={<AlertTriangle size={16} />}
          />

          <SummaryCard
            label="Exigem PI/PO"
            value={resumo.exigemPiPo}
            helper="Clientes com referência obrigatória"
            tone="neutral"
            icon={<ClipboardCheck size={16} />}
          />
        </section>

        <section style={styles.actionBar}>
          <div style={styles.actionBarInfo}>
            <strong>Base de orientação para emissão e envio de notas</strong>
            <span>
              Defina por cliente como emitir a nota, quais referências mencionar
              e para quem enviar XML, DANFSe ou anexos.
            </span>
          </div>

          <div style={styles.actionButtons}>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
            >
              <Plus size={14} />
              Nova regra
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Building2 size={14} />
              Vincular cliente
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Mail size={14} />
              Cadastrar contato
            </button>

            <button type="button" style={styles.secondaryButton}>
              <FileText size={14} />
              Modelo de orientação
            </button>
          </div>
        </section>

        <section style={styles.filterPanel}>
          <div style={styles.filterHeader}>
            <div style={styles.filterTitleArea}>
              <Filter size={15} />
              <strong>Filtros</strong>
            </div>

            <button
              type="button"
              style={styles.clearButton}
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>

          <div style={styles.filterGrid}>
            <div style={styles.searchField}>
              <Search size={14} />
              <input
                style={styles.searchInput}
                value={filters.busca}
                onChange={(event) => updateFilter("busca", event.target.value)}
                placeholder="Buscar por cliente, CNPJ, PI, PO, portal, e-mail ou orientação..."
              />
            </div>

            <select
              style={styles.filterInput}
              value={filters.statusRegra}
              onChange={(event) =>
                updateFilter("statusRegra", event.target.value)
              }
            >
              <option value="">Todos os status</option>
              <option value="ativa">Ativa</option>
              <option value="pendente_revisao">Pendente revisão</option>
              <option value="incompleta">Incompleta</option>
              <option value="inativa">Inativa</option>
              <option value="sem_regra">Sem regra</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.statusValidacao}
              onChange={(event) =>
                updateFilter("statusValidacao", event.target.value)
              }
            >
              <option value="">Todas as validações</option>
              <option value="validada">Validada</option>
              <option value="pendente_validacao">Pendente validação</option>
              <option value="desatualizada">Desatualizada</option>
              <option value="sem_validacao">Sem validação</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.referencia}
              onChange={(event) =>
                updateFilter("referencia", event.target.value)
              }
            >
              <option value="">Todas as referências</option>
              <option value="sc">SC</option>
              <option value="pi">PI</option>
              <option value="po">PO</option>
              <option value="pedido_compra">Pedido de compra</option>
              <option value="ordem_servico_cliente">OS do cliente</option>
              <option value="contrato">Contrato</option>
              <option value="nao_exige">Não exige</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.agrupamento}
              onChange={(event) =>
                updateFilter("agrupamento", event.target.value)
              }
            >
              <option value="">Todos os agrupamentos</option>
              <option value="varios_servicos_mesma_nota">
                Vários serviços na mesma nota
              </option>
              <option value="uma_nota_por_servico">Uma nota por serviço</option>
              <option value="uma_nota_por_pi_po">Uma nota por PI/PO</option>
              <option value="uma_nota_por_embarcacao">
                Uma nota por embarcação
              </option>
              <option value="avaliar_caso_a_caso">Avaliar caso a caso</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.responsavel}
              onChange={(event) =>
                updateFilter("responsavel", event.target.value)
              }
            >
              <option value="">Todos os responsáveis</option>
              {responsaveis.map((responsavel) => (
                <option key={responsavel} value={responsavel}>
                  {responsavel}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Regras fiscais por cliente</h2>
              <span style={styles.tableHint}>
                {filteredItems.length}{" "}
                {plural(filteredItems.length, "registro", "registros")}{" "}
                encontrado(s). Duplo clique na linha abre a orientação completa.
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Referência</th>
                  <th style={styles.th}>Agrupamento</th>
                  <th style={styles.th}>Pré-emissão</th>
                  <th style={styles.th}>Envio / Portal</th>
                  <th style={styles.th}>Revisão</th>
                  <th style={styles.thNoRightBorder}>Pendências</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((regra) => (
                  <RegraClienteRow
                    key={regra.id}
                    regra={regra}
                    departmentColor={activeDepartmentColor}
                    onOpen={setSelectedRegra}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} style={styles.emptyCell}>
                      Nenhuma regra fiscal encontrada com os filtros atuais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegraClienteDetailOverlay
        regra={selectedRegra}
        departmentColor={activeDepartmentColor}
        onClose={() => setSelectedRegra(null)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  summaryCard: {
    minHeight: 78,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "11px 12px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },

  summaryInfo: {
    minWidth: 0,
  },

  summaryIcon: {
    width: 34,
    height: 34,
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconOk: {
    width: 34,
    height: 34,
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconWarning: {
    width: 34,
    height: 34,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconDanger: {
    width: 34,
    height: 34,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryLabel: {
    display: "block",
    fontSize: 11.2,
    fontWeight: 900,
    color: "#64748b",
    lineHeight: 1.15,
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  summaryValue: {
    display: "block",
    marginTop: 3,
    fontSize: 16,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.15,
  },

  summaryHelper: {
    display: "block",
    marginTop: 4,
    fontSize: 11.1,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.2,
  },

  actionBar: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  actionBarInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 260,
    color: "#334155",
    fontSize: 12.4,
    fontWeight: 700,
  },

  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
    flexWrap: "wrap",
  },

  filterPanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
  },

  filterHeader: {
    minHeight: 38,
    padding: "7px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  filterTitleArea: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  clearButton: {
    height: 28,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    padding: "0 9px",
    fontSize: 11.5,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  filterGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1.45fr repeat(5, minmax(132px, 1fr))",
    gap: 8,
  },

  searchField: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 9px",
    minWidth: 0,
  },

  searchInput: {
    border: 0,
    outline: "none",
    height: "100%",
    flex: 1,
    minWidth: 0,
    fontFamily: "inherit",
    fontSize: 12.3,
    fontWeight: 750,
    color: "#111827",
  },

  filterInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 8px",
    fontFamily: "inherit",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    minWidth: 0,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 44,
    padding: "8px 10px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  tableTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 950,
    color: "#111827",
  },

  tableHint: {
    display: "block",
    marginTop: 1,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1320,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  thNoRightBorder: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: 0,
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
    transition: "background 140ms ease, box-shadow 140ms ease",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  tdNoRightBorder: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: 0,
    overflow: "hidden",
  },

  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  clientIcon: {
    width: 29,
    height: 29,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  clientInfo: {
    minWidth: 0,
  },

  clientName: {
    fontSize: 12.8,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mainText: {
    fontSize: 12.4,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mutedText: {
    marginTop: 1,
    display: "block",
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  badgeStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
  },

  documentMini: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11.5,
    fontWeight: 850,
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(1120px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    fontSize: 12.4,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dde5",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  detailContent: {
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  detailTopGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  detailCard: {
    minHeight: 74,
    border: "1px solid #dfe3e8",
    background: "#f8fafc",
    padding: "10px 11px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
  },

  detailLabel: {
    fontSize: 10.8,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  detailValue: {
    marginTop: 3,
    fontSize: 13.2,
    fontWeight: 950,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailMeta: {
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailBadgeArea: {
    marginTop: 7,
  },

  warningBox: {
    border: "1px solid #fed7aa",
    background: "#fff7ed",
  },

  warningHeader: {
    minHeight: 36,
    padding: "8px 10px",
    borderBottom: "1px solid #fed7aa",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#9a3412",
    fontSize: 12.5,
    fontWeight: 950,
  },

  warningList: {
    padding: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    color: "#9a3412",
    fontSize: 12,
    fontWeight: 850,
  },

  detailGridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  detailSection: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    minWidth: 0,
  },

  detailSectionHeader: {
    minHeight: 38,
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  infoGridOne: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  infoItem: {
    minHeight: 52,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  ruleGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 8,
  },

  ruleBox: {
    minHeight: 62,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  orientationBox: {
    margin: "0 10px 10px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  flowList: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  flowItem: {
    display: "grid",
    gridTemplateColumns: "34px 1fr",
    gap: 9,
    alignItems: "flex-start",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    padding: 9,
  },

  flowNumber: {
    width: 28,
    height: 28,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
    flexShrink: 0,
  },

  flowContent: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  flowTitleLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  requiredText: {
    fontSize: 10.8,
    fontWeight: 900,
    color: "#1d4ed8",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    padding: "2px 6px",
  },

  envioIntro: {
    margin: 10,
    marginBottom: 0,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  contactsGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  contactCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 9,
  },

  contactHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },

  contactTitleArea: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  contactMetaGrid: {
    borderTop: "1px solid #edf0f2",
    paddingTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
  },

  contactObservation: {
    borderTop: "1px solid #edf0f2",
    paddingTop: 7,
    color: "#64748b",
    fontSize: 11.7,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  emptyContacts: {
    gridColumn: "1 / -1",
    padding: 16,
    textAlign: "center",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 12.5,
    fontWeight: 750,
  },

  historyList: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  historyItem: {
    display: "grid",
    gridTemplateColumns: "12px 1fr",
    gap: 8,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  historyDot: {
    width: 8,
    height: 8,
    marginTop: 5,
    borderRadius: 999,
    background: "#94a3b8",
  },

  overlayFooter: {
    minHeight: 48,
    padding: "10px 18px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  primaryButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #d7dde5",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  cancelButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
};

export default RegrasFiscaisClientesLayout;
